import * as React from 'react'
import './license'
import { useBulkActionErrors, useSafePaginationModel } from './hooks'
import type { GridComponentProps } from './typings'
import { GridCustomToolbar, GridPagination } from './atoms'
import { pageSizeOptions, sortingOrder } from './constants'
import { useLocalizationDateFormat } from '@user/features/Localization/useLocalizationDateFormat'
import { StyledGrid } from './StyledXGrid.styled'
import { useGridApiRef } from '@mui/x-data-grid-pro'

export const GridComponent: React.FC<GridComponentProps> = (props) => {
  const {
    BulkActions,
    disableInteraction,
    columnsSelectorProps,
    rowSelectionModel = [],
    onRowSelectionModelChange = () => {},
    exportClickCallback = () => {},
    isDensityEnable,
    isExportEnable,
    columns,
    applyCustomStyles,
    virtualScrollerHeight,
    hideDefaultBtn,
    isFilterEnable,
    gridFilterProps,
    exportFileName,
  } = props

  const apiRef = useGridApiRef()

  React.useEffect(() => {
    const timer = setTimeout(() => {
      if (!apiRef.current || !apiRef.current.getAllRowIds) {
        return
      }

      const rootElement = document.querySelector('.MuiDataGrid-root')
      const rootHeight = rootElement?.clientHeight
      const toolbarElement = document.querySelector(
        '.MuiDataGrid-toolbarContainer',
      )

      const toolbarHeight = toolbarElement?.clientHeight

      if (rowSelectionModel.length == 1 && Boolean(BulkActions)) {
        const selectedRowId = rowSelectionModel[0]
        const selectedRowIndex = apiRef.current
          .getAllRowIds()
          .findIndex((row) => row == selectedRowId)

        const rowElement = apiRef.current.getRowElement(selectedRowId)

        if (rowElement) {
          if (rootHeight && toolbarHeight) {
            const containerHeight = rootHeight - toolbarHeight
            const containerRect = rootElement.getBoundingClientRect()
            const rowRect = rowElement.getBoundingClientRect()

            const rowTopRelativeToContainer = rowRect.top - containerRect.top
            const rowBottomRelativeToContainer =
              rowRect.bottom - containerRect.top

            const isVisible =
              rowTopRelativeToContainer >= 0 &&
              rowBottomRelativeToContainer <= containerHeight
            if (!isVisible) {
              const gridContainer = rootElement.querySelector(
                '.MuiDataGrid-virtualScroller',
              )

              if (gridContainer) {
                gridContainer.scroll({
                  top: selectedRowIndex * rowRect.height,
                  behavior: 'smooth',
                })
              }
            }
          }
        }
      }
    }, 100)

    return () => clearTimeout(timer)
  }, [rowSelectionModel, apiRef, BulkActions])

  const { getRowClassName, getCellClassName, columnVisibilityModel } =
    useBulkActionErrors(props)

  const paginationModel = useSafePaginationModel(props.paginationModel)

  const { formatDate, formatDateTime } = useLocalizationDateFormat()

  const localizedColumns = columns?.map((row) => {
    if (row.type == 'date' || row.type == 'dateTime') {
      return {
        ...row,
        valueFormatter: (params) => formatDate(params.value, row.type),
      }
    }
    if (row.type == 'dateTime_12') {
      return {
        ...row,
        valueFormatter: (params) => formatDateTime(params.value),
      }
    }
    return row
  })

  // Merge incoming slots/slotProps with our defaults so callers can add overlays
  const mergedSlots = {
    ...(props as any).slots,
    toolbar: GridCustomToolbar,
    pagination: GridPagination,
  }

  const mergedSlotProps = {
    ...(props as any).slotProps,
    toolbar: {
      ...(props as any).slotProps?.toolbar,
      disableInteraction,
      BulkActions,
      exportClickCallback,
      isDensityEnable,
      isExportEnable,
      hideDefaultBtn,
      gridFilterProps,
      isFilterEnable,
      exportFileName,
    },
    columnsPanel: columnsSelectorProps,
    pagination: { disabled: disableInteraction },
  }

  return (
    <StyledGrid
      applyCustomStyles={applyCustomStyles}
      virtualScrollerHeight={virtualScrollerHeight}
      apiRef={apiRef}
      disableColumnFilter
      disableColumnMenu
      disableRowSelectionOnClick
      pagination
      paginationMode="server"
      pageSizeOptions={pageSizeOptions}
      sortingMode="server"
      sortingOrder={sortingOrder}
      {...props}
      paginationModel={paginationModel}
      columns={localizedColumns}
      getRowClassName={getRowClassName}
      getCellClassName={getCellClassName}
      rowSelectionModel={rowSelectionModel}
      onRowSelectionModelChange={onRowSelectionModelChange}
      checkboxSelection={Boolean(BulkActions)}
      columnVisibilityModel={columnVisibilityModel}
      hideFooterSelectedRowCount
      slots={mergedSlots}
      slotProps={mergedSlotProps}
      hideFooterPagination={disableInteraction}
    />
  )
}
GridComponent.displayName = 'GridComponent'
